import { classNames } from '@vkontakte/vkui';
import { ReactComponent as DocsBannerDecoration } from 'img/unlimited-docs/banner/docs.svg';
import { ReactComponent as PresentationBannerDecoration } from 'img/unlimited-docs/banner/presentation.svg';
import { ReactComponent as SpreadSheetBannerDecoration } from 'img/unlimited-docs/banner/spreadsheet.svg';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { unlimitedDocsFakedoorConfig } from 'reactApp/appHelpers/featuresHelpers/features/unlimitedDocuments';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import {
    getUnlimitedDocumentsBannerStoreValue,
    getUnlimitedDocumentsTypeNameGenitive,
    UNLIMITED_DOCUMENTS_BANNER_ID,
    UNLIMITED_DOCUMENTS_RADAR_NAMES,
    UnlimitedDocumentsEntryPoint,
    UnlimitedDocumentsTariffType,
} from 'reactApp/modules/unlimitedDocuments/unlimitedDocuments.const';
import { UnlimitedDocumentsActions } from 'reactApp/modules/unlimitedDocuments/unlimitedDocuments.module';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './UnlimitedDocumentsBanner.css';

interface UnlimitedDocumentsBannerProps {
    type: UnlimitedDocumentsTariffType;
    setVisible: (visibility: boolean) => void;
}

const decorationsMap: { [k in UnlimitedDocumentsTariffType]: React.FC<React.SVGProps<SVGSVGElement>> } = {
    [UnlimitedDocumentsTariffType.document]: DocsBannerDecoration,
    [UnlimitedDocumentsTariffType.spreadsheet]: SpreadSheetBannerDecoration,
    [UnlimitedDocumentsTariffType.presentation]: PresentationBannerDecoration,
};

export const UnlimitedDocumentsBanner = memo<UnlimitedDocumentsBannerProps>(({ type, setVisible }: UnlimitedDocumentsBannerProps) => {
    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const themeStyle = isDarkTheme ? styles.dark : styles.light;
    const dispatch = useDispatch();

    const handleClick = useCallback(() => {
        setVisible(false);

        const storeValue = getUnlimitedDocumentsBannerStoreValue();
        storeHelper.setValue(UNLIMITED_DOCUMENTS_BANNER_ID, { ...storeValue, state: true });

        sendPaymentGa({
            action: 'click',
            eventCategory: ECategoryGa.banner,
            name: UNLIMITED_DOCUMENTS_RADAR_NAMES.SIDEBAR_BANNER,
            doc_type: type,
        });
        emitAnalyticEvent(AnalyticEventNames.UNLIMITED_DOCUMENTS_SIDEBAR_BANNER_CLICK, { doc_type: type });

        dispatch(
            UnlimitedDocumentsActions.buyUnlimitedDocumentsTariff({
                type: unlimitedDocsFakedoorConfig.type,
                stage: 'start',
                offer_source: UnlimitedDocumentsEntryPoint.SIDEBAR_BANNER,
            })
        );
    }, []);

    useEffect(() => {
        const storeValue = getUnlimitedDocumentsBannerStoreValue();
        storeHelper.setValue(UNLIMITED_DOCUMENTS_BANNER_ID, {
            ...storeValue,
            time: Date.now(),
            counts: { ...storeValue?.counts, show: storeValue?.counts?.show + 1 },
        });

        sendPaymentGa({
            action: 'show',
            eventCategory: ECategoryGa.banner,
            name: UNLIMITED_DOCUMENTS_RADAR_NAMES.SIDEBAR_BANNER,
            doc_type: type,
        });
        emitAnalyticEvent(AnalyticEventNames.UNLIMITED_DOCUMENTS_SIDEBAR_BANNER_SHOW, { doc_type: type });
    }, []);

    const Decoration = decorationsMap[type] || DocsBannerDecoration;

    const text = useMemo(() => {
        const typeNameGenitive = getUnlimitedDocumentsTypeNameGenitive(type);

        return `+ Место для ${typeNameGenitive}`;
    }, [type]);

    return (
        <div className={classNames(styles.root, themeStyle)} onClick={handleClick}>
            <div className={styles.decoration}>
                <Decoration />
            </div>
            {text}
        </div>
    );
});

UnlimitedDocumentsBanner.displayName = 'UnlimitedDocumentsBanner';
